import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Modal,
  useTheme,
  Paper,
} from "@mui/material";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { GiBookshelf } from "react-icons/gi";
import { FaDiagramNext } from "react-icons/fa6";
import { MdConnectWithoutContact } from "react-icons/md";
import { GoLaw } from "react-icons/go";
import { MdOutlineReceiptLong } from "react-icons/md";
import { MdOutlineLiveHelp } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";

// Import modules for modals
import Informate from "../../components/Home/Informe";
import Acceso from "../../components/Home/Acceso";
import DeberesYDerechos from "../../components/Home/DyD";
import Procesos from "../../components/Home/Procesos";
import Contactanos from "../../components/Home/Contactanos";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // Color principal solicitado
  const mainBlue = "#273c7d";

  const [open, setOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const handleOpen = (contentFunc) => {
    setModalContent(contentFunc ? contentFunc(handleClose) : null);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const menuItems = [
    {
      title: "INFÓRMATE",
      icon: <HiOutlineInformationCircle size={32} />,
      modalContent: (handleClose) => <Informate handleClose={handleClose} />,
      description: "Conoce más sobre nuestros servicios y regulaciones.",
    },
    {
      title: "ACCESO A RESOLUCIONES",
      icon: <GiBookshelf size={32} />,
      modalContent: (handleClose) => <Acceso handleClose={handleClose} />,
      description: "Consulta las resoluciones de interés para el usuario.",
    },
    {
      title: "DEBERES Y DERECHOS",
      icon: <GoLaw size={32} />,
      modalContent: (handleClose) => <DeberesYDerechos handleClose={handleClose} />,
      description: "Tus garantías y responsabilidades como usuario.",
    },
    {
      title: "PROCESO DE DENUNCIA",
      icon: <FaDiagramNext size={32} />,
      modalContent: (handleClose) => <Procesos handleClose={handleClose} />,
      description: "Guía paso a paso para realizar un reclamo.",
    },
    {
      title: "REALIZAR DENUNCIA",
      path: "/protecUsuario",
      icon: <MdOutlineReceiptLong size={32} />,
      description: "Inicia oficialmente tu proceso de denuncia aquí.",
    },
    {
      title: "CONTÁCTANOS",
      icon: <MdConnectWithoutContact size={32} />,
      modalContent: (handleClose) => <Contactanos handleClose={handleClose} />,
      description: "Estamos para ayudarte. Ponte en contacto con nosotros.",
    },
    {
      title: "CONSULTAR ESTADO",
      path: "/consultaUsuario",
      icon: <MdOutlineLiveHelp size={32} />,
      description: "Revisa el avance de tus solicitudes vigentes.",
    },
  ];

  return (
    <Box>
      {/* SECTION: HERO / HEADER */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${mainBlue} 0%, #1e2a4a 100%)`,
          py: { xs: 8, md: 10 },
          px: 4,
          mb: 6,
          textAlign: "center",
          color: "white",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: "200px",
            height: "200px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "50%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: "150px",
            height: "150px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "50%",
          }}
        />

        <Typography
          variant="h1"
          fontWeight="800"
          sx={{ mb: 2, fontSize: { xs: "2.5rem", md: "3.5rem" } }}
        >
          Protección al Usuario
        </Typography>
        <Typography
          variant="h4"
          sx={{ opacity: 0.9, maxWidth: "800px", mx: "auto", fontWeight: "300" }}
        >
          Portal oficial de la Comisión Nacional de Telecomunicaciones para la gestión y seguimiento de denuncias y servicios.
        </Typography>
      </Box>

      {/* SECTION: MENU CARDS */}
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: 4, pb: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          {menuItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Paper
                elevation={2}
                onClick={() =>
                  item.path ? navigate(item.path) : handleOpen(item.modalContent)
                }
                sx={{
                  p: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  borderRadius: "16px",
                  cursor: "pointer",
                  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
                  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "0px 20px 40px rgba(0,0,0,0.15)",
                    borderColor: mainBlue,
                    "& .icon-container": {
                      backgroundColor: mainBlue,
                      color: "white",
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                {/* Icon Container */}
                <Box
                  className="icon-container"
                  sx={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.palette.mode === "dark" ? colors.primary[500] : "#f5f7fa",
                    color: mainBlue,
                    mb: 3,
                    transition: "all 0.3s ease",
                  }}
                >
                  {item.icon}
                </Box>

                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ mb: 2, color: theme.palette.mode === "dark" ? "white" : mainBlue }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.grey[300], lineHeight: 1.6 }}
                >
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* MODAL SYSTEM */}
      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "70%", lg: "60%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "16px",
            maxHeight: "90vh",
            overflowY: "auto",
            outline: "none",
          }}
        >
          {modalContent}
        </Box>
      </Modal>
    </Box>
  );
};

export default Dashboard;
